/**
 * @module components/rpdm/FailClosedPlaceholder
 * @summary Uniform "Feature not enabled" / "Capability missing" surface.
 * @description Renders the fail-closed state for any Wave 2 delivery
 *   surface whose feature-flag composition (§ 13.3) evaluates false OR
 *   whose additive-endpoint capability check (§ 4.4) throws
 *   `capability_missing`. UI-I-02 anchor: partial / degraded surfaces
 *   are forbidden — a surface either renders complete OR renders this
 *   placeholder.
 *
 * ## Presentation-only
 * This component does not:
 *   - call `useAuth` or any other context hook,
 *   - dispatch to `sonner`,
 *   - issue any network request,
 *   - translate refusal codes or perform any lookup.
 * All state comes from props; the caller decides who is a super_admin
 * and passes `showDiagnostics={true}` accordingly. This keeps the
 * component fully testable and unmounted-safe (Phase 1b rule).
 *
 * ## Props
 * @param {object} props
 * @param {string} [props.title] - override title. Defaults to a sensible
 *   default keyed on `capabilityMissing` presence.
 * @param {string} [props.message] - explanatory body copy.
 * @param {ReadonlyArray<string>} [props.flagsFailed] - list of feature
 *   flag names that evaluated false. Rendered ONLY when
 *   `showDiagnostics=true` (i.e. for super_admin sessions).
 * @param {string} [props.capabilityMissing] - name of the additive
 *   capability that returned unavailable (`hierarchy`,
 *   `effort_daily_timeseries`, `reachable_states_embed`). When present
 *   the component renders a slightly different message + a11y label.
 * @param {boolean} [props.showDiagnostics] - super_admin diagnostic
 *   panel toggle. Caller performs the role check.
 * @param {() => void} [props.onReturnToWorkspace] - primary CTA click.
 * @param {string} [props.testId] - override `data-testid`.
 *
 * ## Accessibility
 * - `role="status"` on the outer region.
 * - `aria-live="polite"` so screen readers announce the placeholder
 *   without preempting other content.
 * - Diagnostics panel is keyboard-focusable when present.
 * - "Return to workspace" button is a standard `<button>` — keyboard-
 *   activated via Enter / Space.
 *
 * ## Invariants (Phase 1b)
 * - No existing surface renders this component. Unmounted in-repo.
 * - Zero network activity; no side effects.
 * - Tree-shakeable named export.
 *
 * ## Future consuming phase
 * - Phase 2+: every delivery surface calls
 *   `<FailClosedPlaceholder flagsFailed=... capabilityMissing=... .../>`
 *   when its gate is false.
 */

import React from "react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

export const FailClosedPlaceholder = React.forwardRef(function FailClosedPlaceholder(
    props,
    ref,
) {
    const {
        title,
        message,
        flagsFailed,
        capabilityMissing,
        showDiagnostics,
        onReturnToWorkspace,
        testId,
    } = props || {};

    const derivedTitle = title
        || (capabilityMissing ? "Feature not available on this environment" : "Feature not enabled");
    const derivedMessage = message
        || (capabilityMissing
            ? "The required backend endpoint has not been enabled here. This surface remains fail-closed until it ships."
            : "This feature is disabled for the current session. Reach out to an administrator if you believe this is a mistake.");

    const rootTestId = testId
        || (capabilityMissing
            ? "rpdm-failclosed-capability-" + capabilityMissing
            : "rpdm-failclosed-placeholder");

    return (
        <div
            ref={ref}
            role="status"
            aria-live="polite"
            data-testid={rootTestId}
            className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-6 py-16"
        >
            <Alert data-testid={rootTestId + "-alert"}>
                <AlertTitle data-testid={rootTestId + "-title"}>
                    {derivedTitle}
                </AlertTitle>
                <AlertDescription data-testid={rootTestId + "-body"}>
                    {derivedMessage}
                </AlertDescription>
            </Alert>

            {showDiagnostics && Array.isArray(flagsFailed) && flagsFailed.length > 0 ? (
                <div
                    data-testid={rootTestId + "-diagnostics"}
                    className="rounded-md border border-dashed p-4 text-sm"
                >
                    <div className="mb-2 font-medium text-muted-foreground">
                        Diagnostic detail (visible to super-admin only)
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {flagsFailed.map((name) => (
                            <Badge
                                key={name}
                                variant="secondary"
                                data-testid={rootTestId + "-flag-" + name}
                            >
                                {name}
                            </Badge>
                        ))}
                    </div>
                    {capabilityMissing ? (
                        <div className="mt-3 text-xs text-muted-foreground">
                            Missing capability: <code>{capabilityMissing}</code>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {typeof onReturnToWorkspace === "function" ? (
                <div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onReturnToWorkspace}
                        data-testid={rootTestId + "-return-btn"}
                    >
                        Return to workspace
                    </Button>
                </div>
            ) : null}
        </div>
    );
});
